import { useEffect, useRef } from "react";

import type { ScrollGroupItem } from "./context";
import { useScrollGroupContext } from "./context";
import { useScrollGroupSetScroll } from "./useScrollGroupSetScroll";

/**
 * Register a ref for the scroll group. The element within the ref will have its
 * scroll synced with all other items in the group. Remember to memoize the item.
 */
export function useScrollGroupItem(item: ScrollGroupItem) {
  const {
    registerItem,
    scrollGroupItemsRef,
    currentManualScrollMetaRef,
    onScrollItem,
    onAddItem,
    onResizeItem,
  } = useScrollGroupContext();

  useEffect(() => {
    const unregisterItem = registerItem(item);
    return () => {
      unregisterItem();
    };
  }, [item, registerItem]);

  useEffect(() => {
    const element = item.refObject.current;
    if (!element) return;

    const handler = (event: Event) => {
      onScrollItem?.(element, event);
    };
    element.addEventListener("scroll", handler);
    return () => {
      element.removeEventListener("scroll", handler);
    };
  }, [item, item.refObject, onScrollItem]);

  const onAddItemRef = useRef(onAddItem);
  useEffect(() => {
    onAddItemRef.current = onAddItem;
  }, [onAddItem]);
  useEffect(() => {
    const element = item.refObject.current;
    if (element) {
      onAddItemRef.current?.(element);
    }
    return undefined;
  }, [item, item.refObject, onAddItem]);

  useEffect(() => {
    const element = item.refObject.current;
    if (!element) {
      return undefined;
    }

    const handler = () => {
      onResizeItem?.(element);
    };
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
    };
  }, [item.refObject, onResizeItem]);

  const scrollAllTo = useScrollGroupSetScroll();

  useEffect(() => {
    const thisElement = item.refObject.current;
    if (!thisElement) {
      return;
    }

    const handler = () => {
      if (!scrollGroupItemsRef.current) {
        return;
      }

      // Handler for scroll event of this specific item
      // This could be triggered in two ways, either programmatically (scrollTo) or by the user scrolling manually
      // We need to branch out depending on if useScrollGroupTo has been used (then currentManualScrollMetaRef will be set) or not

      if (!currentManualScrollMetaRef.current) {
        // Scroll was not triggered programmatically
        // Just set all scroll containers to the same values as this
        scrollAllTo({
          top: thisElement.scrollTop,
          left: thisElement.scrollLeft,
        });
      }
    };

    thisElement.addEventListener("scroll", handler);
    return () => {
      thisElement.removeEventListener("scroll", handler);
    };
  }, [
    currentManualScrollMetaRef,
    item.refObject,
    scrollAllTo,
    scrollGroupItemsRef,
  ]);
}
